import { createContext, useContext } from 'react';

import type { ProgressStore } from './progress/store';
import type { GuidePageMatch } from './safeRoutes';
import type { GuidanceStore } from './store';
import type {
  CoachDefinition,
  GuidanceContext,
  GuidanceScope,
  Portal,
  TourDefinition,
} from './types';

/**
 * The public face of `GuidanceRoot`: which portal, and whose scope. Exactly
 * this shape — shells and pages rely on it.
 */
export interface GuidanceRootValue {
  portal: Portal;
  scope: GuidanceScope;
}

export const GuidanceRootContext = createContext<GuidanceRootValue | null>(null);

/**
 * The enclosing `GuidanceRoot`, or `null` outside one — on sign-in, MFA,
 * activation and secure-link screens, where guidance must not exist at all.
 */
export function useGuidanceRoot(): GuidanceRootValue | null {
  return useContext(GuidanceRootContext);
}

/**
 * What the guidance UI itself reads (the Help entry and panel, the invitation):
 * the context tour predicates see, the in-memory state, the stored progress,
 * the catalogue and the page the user is on.
 *
 * Built ONCE, in the shell, from hooks the shell already runs — so pieces that
 * mount later add no observer and no request of their own.
 */
export interface GuidanceInternals extends GuidanceRootValue {
  ctx: GuidanceContext;
  store: GuidanceStore;
  progress: ProgressStore;
  catalogue: readonly TourDefinition[];
  /**
   * The guided workflow coaches (Layer B), filtered per person by `coach.ts`.
   * Optional so a test's hand-made internals need not list them: none, then.
   */
  coaches?: readonly CoachDefinition[];
  /** The guide page the user is on, or `null` where no guidance may run. */
  page: GuidePageMatch | null;
}

export const GuidanceInternalsContext = createContext<GuidanceInternals | null>(null);

export function useGuidanceInternals(): GuidanceInternals | null {
  return useContext(GuidanceInternalsContext);
}
